import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAlert } from "../../contexts/AlertProvider";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import { API_BASE_URL } from "../../config";
import { useTokens } from "../../theme";

export default function ResetPasswordDialog({ dialogProps }) {
  const { open, dialogHandlers } = dialogProps;
  const tokens = useTokens();
  const [isLoading, setIsLoading] = React.useState(false);
  const [validationError, setValidationError] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState({ email: "" });
  const { alert } = useAlert();

  const handleEmailChange = (value) => {
    setResetEmail({ email: value });
  };

  const sendResetPasswordEmail = async () => {
    const options = {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...resetEmail }),
    };

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/agents/reset-password/send-email/v1`,
        options
      );

      const result = await response.json();

      if (result.status !== "SUCCESS") {
        alert({
          title: "Failed to Send Reset Password Email",
          detail: result.failureReason,
          severity: "warning",
          duration: 5000,
        });
        return;
      }

      const { email } = result.data;

      alert({
        title: "Reset Email Sent",
        detail: `Reset Password Email was sent to ${email}`,
        severity: "success",
        duration: 5000,
      });
    } catch (error) {
      alert({
        title: "Error Occurred",
        detail: "Unable to send reset password email at the moment",
        severity: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    let hasError = resetEmail.email === "";
    setValidationError(hasError);
  }, [resetEmail.email]);

  return (
    <>
      <Dialog open={open} onClose={dialogHandlers.close}>
        <DialogTitle>
          Reset Password
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontSize: "0.875rem", color: tokens.mutedForeground, mb: 2 }}
          >
            Enter the email associated with your account and we will send you a
            link with steps on how to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            id="name"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            autoComplete="email"
            onChange={(event) => handleEmailChange(event.target.value)}
            variant="outlined"
            error={resetEmail.email === ""}
            helperText={resetEmail.email === "" && "Email address is required"}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="large"
            startIcon={<CancelIcon />}
            onClick={dialogHandlers.close}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="large"
            loading={isLoading}
            startIcon={<SendIcon />}
            disabled={validationError}
            onClick={sendResetPasswordEmail}
          >
            Reset Password
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
